import { readCache, writeCache, describeRequest } from '../../utils/cache.js';
import { logger } from '../../utils/logger.js';
import { recordCreditUsage } from './credit-usage.js';
import { estimateRestCredits } from './credit-costs.js';

export const DRISHTI_BASE_URL = process.env.DRISHTI_BASE_URL || 'https://developers.manasija.in';

export type DrishtiQueryValue =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | boolean[]
  | null
  | undefined;

export type DrishtiQuery = Record<string, DrishtiQueryValue>;

export interface DrishtiResponse<T> {
  data: T;
  url: string;
}

export interface DrishtiRequestOptions {
  cacheable?: boolean;
  ttlMs?: number;
  timeoutMs?: number;
  retries?: number;
  headers?: Record<string, string>;
}

export class DrishtiApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
    readonly url: string,
  ) {
    super(message);
    this.name = 'DrishtiApiError';
  }
}

const DEFAULT_TIMEOUT_MS = 20_000;
const DEFAULT_RETRIES = 2;
const RETRY_STATUSES = new Set([408, 409, 425, 429, 500, 502, 503, 504]);

function getApiKey(): string {
  return process.env.DRISHTI_API_KEY || '';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function retryDelayMs(response: Response | null, attempt: number): number {
  const retryAfter = response?.headers.get('retry-after');
  if (retryAfter) {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds)) return seconds * 1000;

    const dateMs = Date.parse(retryAfter);
    if (Number.isFinite(dateMs)) return Math.max(0, dateMs - Date.now());
  }

  return Math.min(2_000, 250 * 2 ** attempt);
}

function appendQuery(url: URL, params: DrishtiQuery): void {
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        url.searchParams.append(key, String(item));
      }
    } else {
      url.searchParams.append(key, String(value));
    }
  }
}

function cacheParams(params: DrishtiQuery): Record<string, string | number | boolean | string[] | number[] | boolean[] | undefined> {
  const cleaned: Record<string, string | number | boolean | string[] | number[] | boolean[] | undefined> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === null) continue;
    cleaned[key] = value;
  }
  return cleaned;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function shouldTrackCredits(endpoint: string): boolean {
  return !endpoint.startsWith('/v1/account/');
}

async function execute<T>(
  method: 'GET' | 'POST',
  endpoint: string,
  url: URL,
  init: RequestInit,
  params: DrishtiQuery,
  options: DrishtiRequestOptions = {},
): Promise<DrishtiResponse<T>> {
  const apiKey = getApiKey();
  const label = `${method} ${describeRequest(endpoint, cacheParams(Object.fromEntries(url.searchParams)))}`;

  if (!apiKey) {
    logger.warn(`[Drishti API] call without DRISHTI_API_KEY: ${label}`);
  }

  const retries = options.retries ?? DEFAULT_RETRIES;
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
    let response: Response | null = null;

    try {
      response = await fetch(url, {
        ...init,
        signal: controller.signal,
        headers: {
          'X-API-Key': apiKey,
          Accept: 'application/json',
          ...options.headers,
          ...init.headers,
        },
      });

      const body = await parseResponseBody(response);

      if (response.ok) {
        if (shouldTrackCredits(endpoint)) {
          recordCreditUsage(estimateRestCredits(endpoint, params, body));
        }
        return { data: body as T, url: url.toString() };
      }

      const error = new DrishtiApiError(
        `[Drishti API] ${label} failed: ${response.status} ${response.statusText}`,
        response.status,
        body,
        url.toString(),
      );

      if (!RETRY_STATUSES.has(response.status) || attempt === retries) {
        throw error;
      }

      lastError = error;
      await sleep(retryDelayMs(response, attempt));
    } catch (error) {
      lastError = error;
      if (attempt === retries) break;
      await sleep(retryDelayMs(response, attempt));
    } finally {
      clearTimeout(timeout);
    }
  }

  const message = lastError instanceof Error ? lastError.message : String(lastError);
  logger.error(`[Drishti API] ${label} failed: ${message}`);
  throw lastError instanceof Error ? lastError : new Error(message);
}

export const drishtiApi = {
  async get<T>(
    endpoint: string,
    params: DrishtiQuery = {},
    options: DrishtiRequestOptions = {},
  ): Promise<DrishtiResponse<T>> {
    const cleanedParams = cacheParams(params);
    if (options.cacheable) {
      const cached = readCache(endpoint, cleanedParams, options.ttlMs);
      if (cached) {
        if (shouldTrackCredits(endpoint)) {
          recordCreditUsage(0);
        }
        return cached as DrishtiResponse<T>;
      }
    }

    const url = new URL(endpoint, DRISHTI_BASE_URL);
    appendQuery(url, params);

    const result = await execute<T>('GET', endpoint, url, {}, cleanedParams, options);

    if (options.cacheable) {
      writeCache(endpoint, cleanedParams, result.data as Record<string, unknown>, result.url);
    }

    return result;
  },

  async post<T>(
    endpoint: string,
    body: unknown,
    options: DrishtiRequestOptions = {},
  ): Promise<DrishtiResponse<T>> {
    const url = new URL(endpoint, DRISHTI_BASE_URL);
    return execute<T>(
      'POST',
      endpoint,
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      {},
      options,
    );
  },
};
